import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  FormLabel,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { FaPlusCircle } from "react-icons/fa";
import { useSearchParams, Link } from "react-router-dom";
import ThankYouPopup from "./models/thankyou";
import { CREATE_ORDER_URL } from "../utils";

const FormComponent = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trackingId, setTrackingId] = useState("Not available right now");
  const validationSchema = Yup.object().shape({
    side: Yup.string().required("The card's is required"),
    bride_name: Yup.string().required("Bride name is required"),
    groom_name: Yup.string().required("Groom name is required"),
    bride_mother: Yup.string().required("Bride mother name is required"),
    groom_mother: Yup.string().required("Groom mother name is required"),
    bride_father: Yup.string().required("Bride father name is required"),
    groom_father: Yup.string().required("Groom father name is required"),
    contact_no: Yup.string()
      .required("Contact number is required")
      .matches(/^[0-9]{10}$/, "Contact number must be 10 digits"),
    email: Yup.string().email("Invalid email format").required("Email is required"),
    music_link: Yup.string().url("Invalid URL format").optional(),
    rsvp: Yup.string().required("RSVP is required"),
    comment: Yup.string().optional(),
    no_of_event: Yup.number()
      .required("Number of events is required")
      .min(1, "At least one event is required"),
    events: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Event name is required"),
        venue: Yup.string().required("Event venue is required"),
        time: Yup.string().required("Event time is required"),
        address: Yup.string().required("Event address is required"),
      })
    ),
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    register,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      side: "",
      bride_name: "",
      groom_name: "",
      bride_mother: "",
      groom_mother: "",
      bride_father: "",
      groom_father: "",
      contact_no: "",
      music_link: "",
      rsvp: "",
      comment: "",
      no_of_event: 1,
      events: [{ name: "", venue: "", time: "", address: "" }],
    },
  });

  const previewCart = JSON.parse(localStorage.getItem("cart"));

  const { fields, append } = useFieldArray({
    control,
    name: "events",
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      no_of_event: data?.events?.length || 1,
      og_owner_id: 1,
      owner_id: 1,
      details: JSON.stringify({ id }),
      comment: data.comment,
      side: data?.side,
      events: data?.events?.map((fun) => {
        return {
          ...fun,
          time: `${fun.date}T${fun.time}`,
        };
      }),
    };
    try {
      const response = await fetch(CREATE_ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setPopupOpen(true);
        const data = await response.json();
        setTrackingId(data?.tracking_id?.split("TRK-")[1]);
      } else {
        alert("Error creating order.");
      }
      setLoading(false);
    } catch (error) {
      alert("Network error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        maxWidth: "800px",
        margin: "auto",
        padding: "20px",
        marginTop: 6,
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h4"
        style={{ marginBottom: 24, fontWeight: 600, color: "#d4a256" }}
        align="center"
        gutterBottom
      >
        Wedding Invitation Booking Form
      </Typography>
      <Grid container spacing={2}>
        <Grid item xl={4} lg={4} />
        <Grid item xl={4} lg={4}>
          <iframe
            src={`https://drive.google.com/file/d/${id}/preview`}
            height="300"
            allow="autoplay"
            style={{ border: "none", width: "80%" }}
            title="edigital Video"
          ></iframe>
        </Grid>
        <Grid item xl={4} lg={4} />
      </Grid>

      <Link to="/videos">Change the theme</Link>
      <Grid container spacing={2}  style={{ marginBottom: 12, marginTop: 12 }}>
        <Grid item xl={12} style={{width: '100%'}}>
          <FormControl fullWidth>
            <Select
              labelId="card-side-label"
              id="card-side"
              defaultValue=""
              displayEmpty
              name="side"
              placeholder="Select Card's Side"
              {...register("side")}
            >
              <MenuItem value="" disabled>
                Select Card's Side
              </MenuItem>
              <MenuItem value="B">Bride's Side</MenuItem>
              <MenuItem value="G">Groom's Side</MenuItem>
              <MenuItem value="O">Other</MenuItem>
            </Select>
            {errors?.side && (
              <span style={{ color: "red", fontSize: 12 }}>
                Select Card's Side
              </span>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Text Fields */}
        {[
          {
            label: "Bride Name",
            name: "bride_name",
            placeholder: "Enter Bride Name",
          },
          {
            label: "Groom Name",
            name: "groom_name",
            placeholder: "Enter Groom Name",
          },
          {
            label: "Bride's Mother",
            name: "bride_mother",
            placeholder: "Enter Bride's Mother Name",
          },
          {
            label: "Groom's Mother",
            name: "groom_mother",
            placeholder: "Enter Groom's Mother Name",
          },
          {
            label: "Bride's Father",
            name: "bride_father",
            placeholder: "Enter Bride's Father Name",
          },
          {
            label: "Groom's Father",
            name: "groom_father",
            placeholder: "Enter Groom's Father Name",
          },
          {
            label: "WhatsApp Number",
            name: "contact_no",
            placeholder: "Enter WhatsApp Number",
          },
          {
            label: "Email Address",
            name: "email",
            optional: true,
            placeholder: "Enter Email Address",
          },
          {
            label: "Music Link",
            name: "music_link",
            optional: true,
            placeholder: "Youtube or reels link",
          },
          {
            label: "RSVP",
            name: "rsvp",
            placeholder: "Enter family members name",
          },
          {
            label: "Comment",
            name: "comment",
            optional: true,
            placeholder: "Any suggations you want add !",
          },
        ].map(({ label, name, optional, placeholder }) => (
          <Grid item xs={12} key={name}>
            <TextField
              label={label}
              fullWidth
              {...register(name)}
              error={!!errors[name]}
              placeholder={placeholder}
              helperText={errors[name]?.message}
              required={!optional}
            />
          </Grid>
        ))}

        {/* Events */}
        <Grid item xs={12}>
          <Typography variant="h6">Event Details</Typography>
          {fields.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                marginTop: 3,
                paddingBottom: 2,
                borderBottom: "1px solid #ccc",
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {`Event ${index + 1}`}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Event Name"
                    fullWidth
                    {...register(`events.${index}.name`)}
                    error={!!errors.events?.[index]?.name}
                    helperText={errors.events?.[index]?.name?.message}
                    required
                    placeholder="Eg: Haldi, Mehnid, Sangeet, Wedding....."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Venue"
                    fullWidth
                    {...register(`events.${index}.venue`)}
                    error={!!errors.events?.[index]?.venue}
                    helperText={errors.events?.[index]?.venue?.message}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    label="Address"
                    fullWidth
                    {...register(`events.${index}.address`)}
                    error={!!errors.events?.[index]?.address}
                    helperText={errors.events?.[index]?.address?.message}
                    placeholder="Any suggations from your end"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormLabel>Pick the correct Date & Time of event</FormLabel>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <input
                    label="Date"
                    fullWidth
                    {...register(`events.${index}.date`)}
                    error={!!errors.events?.[index]?.time}
                    helperText={errors.events?.[index]?.time?.message}
                    required
                    type="date"
                    placeholder="Select Date"
                    min={new Date().toISOString().split("T")[0]}
                    style={{
                      height: 45,
                      borderRadius: 4,
                      borderColor: "lightgray",
                      width: "40%",
                    }}
                  />
                  <input
                    placeholder="Select Time"
                    fullWidth
                    {...register(`events.${index}.time`)}
                    error={!!errors.events?.[index]?.time}
                    helperText={errors.events?.[index]?.time?.message}
                    required
                    style={{
                      height: 45,
                      borderRadius: 4,
                      borderColor: "lightgray",
                      width: "40%",
                      marginLeft: "5%",
                    }}
                    type="time"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
          <IconButton
            color="primary"
            onClick={() =>
              append({ name: "", venue: "", time: "", address: "" })
            }
            sx={{ mt: 2 }}
          >
            <FaPlusCircle size={20} style={{ marginRight: 4 }} />
            <Typography>Add Event</Typography>
          </IconButton>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            style={{ background: "#d4a256" }}
          >
            {loading ? <CircularProgress color="#fff" /> : "Submit"}
          </Button>
        </Grid>
        {/* Modal */}
        <ThankYouPopup
          open={isPopupOpen}
          number={getValues("contact_no")}
          trackingId={trackingId}
          onClose={() => {
            setPopupOpen(false);
            reset();
          }}
        />
      </Grid>
    </Box>
  );
};

export default FormComponent;
